import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, PlayCircle, Check, ChevronRight } from "lucide-react";
import { Header } from "@/components/Header";
import { PublicFooter } from "@/components/PublicFooter";
import { SEO, SITE_URL } from "@/components/SEO";
import { getSolution, SOLUTIONS } from "@/lib/solutions";

export default function Solution() {
  const { slug } = useParams();
  const sol = getSolution(slug);
  if (!sol) return <Navigate to="/solutions" replace />;

  const canonical = `${SITE_URL}/solutions/${sol.slug}`;
  const others = SOLUTIONS.filter((s) => s.slug !== sol.slug);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Customize Studio",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      description: sol.metaDescription,
      url: canonical,
      offers: { "@type": "Offer", price: "149.99", priceCurrency: "USD" },
      keywords: sol.keyword,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: sol.faqs.map(([q, a]) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: sol.howToTitle,
      step: sol.howTo.map(([name, text], i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name,
        text,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Solutions", item: `${SITE_URL}/solutions` },
        { "@type": "ListItem", position: 3, name: sol.keyword, item: canonical },
      ],
    },
  ];

  return (
    <div className="min-h-screen" data-testid={`solution-page-${sol.slug}`}>
      <SEO title={sol.title} description={sol.metaDescription} canonical={canonical} jsonLd={jsonLd} />
      <Header />

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-6 py-16 md:px-10 lg:py-24">
        <nav className="flex items-center gap-1.5 text-xs text-[var(--ink-soft)]" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[var(--brand)]">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/solutions" className="hover:text-[var(--brand)]">Solutions</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-[var(--ink)]">{sol.keyword}</span>
        </nav>
        <p className="overline mt-6">{sol.keyword}</p>
        <h1 data-testid="solution-h1" className="mt-3 font-display text-[2rem] font-semibold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
          {sol.hero}
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[var(--ink-soft)]">{sol.subhero}</p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link to="/signup" data-testid="solution-cta-signup"
            className="flex items-center gap-2 rounded-full bg-[var(--brand)] px-6 py-3.5 text-sm font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-px hover:bg-[var(--brand-dark)]">
            Get Started <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
          <Link to="/demo" data-testid="solution-cta-demo"
            className="flex items-center gap-2 rounded-full border border-[var(--line)] bg-white px-6 py-3.5 text-sm font-semibold transition-transform duration-200 ease-out hover:-translate-y-px">
            <PlayCircle className="h-4 w-4 text-[var(--brand)]" strokeWidth={1.75} /> See a live demo
          </Link>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-4xl px-6 md:px-10">
        <div className="space-y-5 text-base leading-relaxed text-[var(--ink-soft)]">
          {sol.intro.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-4xl px-6 py-16 md:px-10">
        <h2 className="font-display text-3xl tracking-tight md:text-4xl">Why teams choose Customize Studio</h2>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
          {sol.features.map(([t, d]) => (
            <div key={t} className="rounded-2xl border border-[var(--line)] bg-white p-6">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--brand)]/10">
                <Check className="h-4 w-4 text-[var(--brand)]" strokeWidth={2.5} />
              </div>
              <p className="mt-3 font-semibold">{t}</p>
              <p className="mt-1 text-sm text-[var(--ink-soft)]">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How to */}
      <section className="border-y border-[var(--line)] bg-[var(--surface-2)]">
        <div className="mx-auto max-w-4xl px-6 py-16 md:px-10">
          <h2 className="font-display text-3xl tracking-tight md:text-4xl">{sol.howToTitle}</h2>
          <ol className="mt-8 space-y-5">
            {sol.howTo.map(([name, text], i) => (
              <li key={name} className="flex gap-4">
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[var(--brand)] text-sm font-bold text-white">{i + 1}</span>
                <div>
                  <p className="font-semibold">{name}</p>
                  <p className="mt-0.5 text-sm text-[var(--ink-soft)]">{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-6 py-16 md:px-10">
        <h2 className="font-display text-3xl tracking-tight md:text-4xl">Frequently asked questions</h2>
        <div className="mt-8 divide-y divide-[var(--line)] border-t border-[var(--line)]">
          {sol.faqs.map(([q, a]) => (
            <details key={q} className="group py-5" data-testid="solution-faq">
              <summary className="flex cursor-pointer list-none items-center justify-between font-semibold">
                {q}
                <ChevronRight className="h-4 w-4 flex-none text-[var(--ink-soft)] transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-[var(--ink-soft)]">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-6 pb-16 md:px-10">
        <div className="rounded-3xl bg-[var(--ink)] px-8 py-12 text-center text-white md:py-16">
          <h2 className="font-display text-3xl tracking-tight md:text-4xl">See the finished project before you build it</h2>
          <p className="mx-auto mt-3 max-w-xl text-white/70">Get started and render your first project in minutes.</p>
          <Link to="/signup" data-testid="solution-cta-bottom"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-200 ease-out hover:-translate-y-px hover:bg-[var(--brand-dark)]">
            Get Started <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
      </section>

      {/* Related */}
      <section className="mx-auto max-w-4xl px-6 pb-16 md:px-10">
        <p className="overline">Explore more</p>
        <div className="mt-4 flex flex-wrap gap-3">
          {others.map((s) => (
            <Link key={s.slug} to={`/solutions/${s.slug}`} data-testid={`related-${s.slug}`}
              className="rounded-full border border-[var(--line)] bg-white px-4 py-2 text-sm font-medium transition-colors hover:border-[var(--brand)] hover:text-[var(--brand)]">
              {s.keyword}
            </Link>
          ))}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
